import { Table as AntTable } from 'antd';
import { SizeType } from 'antd/es/config-provider/SizeContext';
import classNames from 'classnames';
import moment from 'moment';
import React, { FC, useCallback, useMemo, useState } from 'react';
import {
  arrayMove,
  SortableContainer,
  SortableElement,
} from 'react-sortable-hoc';
import useDeepCompareEffect from 'use-deep-compare-effect';
import { ProjectId, TaskId } from 'api/models';
import { Avatar, AvatarGroup } from 'components/atoms/avatar';
import { DragHandle } from 'components/atoms/drag-handle';
import { DueDate } from 'components/atoms/due-date';
import { SubTaskMoreActions } from 'components/atoms/sub-task-more-actions';
import {
  optionsToSquadPayload,
  squad,
  squadToOptions,
} from 'components/molecules/squad-picker/utils';
import { TaskNameCell } from 'components/molecules/tasks/task-name-cell';
import { RbacTaskAssigneePicker } from 'components/organisms/rbac/components/rbac-assignee-pickers';
import { RbacCustomDueDatePicker } from 'components/organisms/rbac/components/rbac-custom-duedate-picker';
import { useProjectDetail } from 'data/projects';
import { Task } from 'data/tasks';
import { ProjectResource, useWithAccess } from 'hooks/rbac/useWithAccess';
import { useTranslation } from 'hooks/use-translation';
import styles from './tasks-table.module.css';

interface Props {
  size: SizeType;
  handleAssigneeChange: (
    taskId: number,
    selectedAssignee,
    callback?: (value) => void,
  ) => void;
  onTaskNameClick: (taskId: TaskId) => void;
  handleDueDateChange: (
    taskId: number,
    payload: {
      startDate?: number;
      dueDate?: number;
    },
  ) => void;
  handleDelete: (task: Task) => void;
  data: Task[];
  onSort?: (sorted: Task[]) => void;
  isSubTask?: boolean;
  isReadOnly?: boolean;
  isSuPublished?: boolean;
  projectId: number;
  isFromDocument: boolean;
  handleRemoveAsSubtask: (task: Task) => void;
}

const DragHandleCell = {
  dataIndex: 'sort',
  width: 40,
  className: classNames('drag-visible', styles.drag_handle),
  render: () => <DragHandle />,
};

const SortableItem = SortableElement((props) => <tr {...props} />);
const Sortable = SortableContainer((props) => <tbody {...props} />);

export const Table: FC<Props> = ({
  data,
  handleDueDateChange,
  handleAssigneeChange,
  handleDelete,
  size,
  onTaskNameClick,
  onSort,
  isSubTask,
  isReadOnly,
  isSuPublished,
  projectId,
  isFromDocument,
  handleRemoveAsSubtask,
}) => {
  const assigneeLabel = useTranslation('assigned_to');
  const onHandleDueDateChange = (
    selectedStartDate,
    selectedDueDate,
    task: Task,
  ) => {
    const payload: { [key: string]: number } = {};

    if (selectedStartDate !== undefined) {
      payload.startDate = selectedStartDate;
    }
    if (selectedDueDate !== undefined) {
      payload.dueDate = selectedDueDate;
    }
    handleDueDateChange(task.taskId, payload);
  };

  const { data: projectDetail } = useProjectDetail(ProjectId(projectId));

  const projectResource = useMemo(
    () => (projectDetail ? ProjectResource(projectDetail) : undefined),
    [projectDetail],
  );

  const { hasAccessToResource } = useWithAccess();

  const hasWriteAccessForTaskActions = hasAccessToResource({
    rule: isSubTask
      ? 'SUBTASK:WRITE'
      : isFromDocument
      ? // ? 'DOCUMENT_TASK:WRITE'
        'TASK:WRITE'
      : 'TASK:WRITE',
    resourceData: projectResource,
  });
  const hasDeleteAccessForTaskActions = hasAccessToResource({
    rule: isSubTask
      ? 'SUBTASK:WRITE'
      : isFromDocument
      ? // ? 'DOCUMENT_TASK:WRITE'
        'TASK:WRITE'
      : 'TASK:WRITE',
    resourceData: projectResource,
  });

  const COLUMNS = [
    {
      title: useTranslation('task_name'),
      className: classNames(styles.task_name, 'drag-visible task-name'),
      dataIndex: 'taskName',
      key: 'taskName',
      width: isReadOnly ? '70%' : '50%',
      render: (text, record: Task) => (
        <TaskNameCell
          size={size}
          taskName={record.taskName}
          taskType={record.type}
          fields={record.fields}
          atRisk={record.atRisk}
          progress={record.progress}
          taskId={TaskId(record.project, record.taskId)}
          onTaskNameClick={isReadOnly ? undefined : onTaskNameClick}
          isSubTask={isSubTask}
          disableStatusTrigger={isSuPublished}
          projectId={record.project}
        />
      ),
    },
    ...(!isReadOnly
      ? [
          {
            title: assigneeLabel,
            className: styles.assignee,
            width: '20%',
            render: (value, record: Task) =>
              record.project ? (
                !isSuPublished ? (
                  <RbacTaskAssigneePicker
                    isMini
                    readOnly={isReadOnly}
                    isPrivate={record.private}
                    onChange={(selected, resolveCallback?: (value) => void) =>
                      handleAssigneeChange(
                        record.taskId,
                        optionsToSquadPayload(selected),
                        resolveCallback,
                      )
                    }
                    projectId={ProjectId(record.project)}
                    value={squadToOptions(squad(record.assignee))}
                    startDate={record.startDate}
                    dueDate={record.dueDate}
                    rule={
                      isSubTask
                        ? 'SUBTASK:WRITE'
                        : isFromDocument
                        ? // ? 'DOCUMENT_TASK:WRITE'
                          'TASK:WRITE'
                        : 'TASK:WRITE'
                    }
                    resourceData={projectResource}
                    propsIfNoAccess={{
                      isDisabled: true,
                    }}
                  />
                ) : (
                  <div className={styles.cell}>
                    <AvatarGroup maxCount={2}>
                      {squadToOptions(squad(record.assignee)).map((v) => (
                        <Avatar name={v.label} src={v.imageUrl} />
                      ))}
                    </AvatarGroup>
                  </div>
                )
              ) : null,
          },
        ]
      : []),
    {
      title: useTranslation('due_date'),
      className: styles.due_date,
      width: '30%',
      render: (text, record: Task) =>
        !isSuPublished && !isReadOnly ? (
          <RbacCustomDueDatePicker
            disabled={!record.taskId || isReadOnly}
            onDateChange={([start, due]) =>
              onHandleDueDateChange(start, due, record)
            }
            size="small"
            startDate={record.startDate}
            dueDate={record.dueDate}
            rule={
              isSubTask
                ? 'SUBTASK:WRITE'
                : isFromDocument
                ? // ? 'DOCUMENT_TASK:WRITE'
                  'TASK:WRITE'
                : 'TASK:WRITE'
            }
            resourceData={projectResource}
            propsIfNoAccess={{
              disabled: true,
            }}
          />
        ) : (
          <div className={styles.cell}>
            {record?.dueDate ? (
              <DueDate
                timestamp={moment(record.dueDate)}
                completed={Boolean(record.completedAt)}
              />
            ) : null}
          </div>
        ),
    },
    ...(!isSuPublished && !isReadOnly
      ? [
          {
            width: '32px',
            className: styles.more_options,
            render: (text, record: Task) =>
              isSuPublished ? null : (
                <SubTaskMoreActions
                  data={record}
                  onDelete={(task) => handleDelete(task)}
                  disabledAction={
                    !record?.taskId || !hasWriteAccessForTaskActions
                  }
                  onRemoveAsSubtask={(task) => handleRemoveAsSubtask(task)}
                  disableDuplicateTasks
                  disableDeleteTask={!hasDeleteAccessForTaskActions}
                />
              ),
          },
        ]
      : []),
  ];

  const [dataSource, setDataSource] = useState<Task[]>([]);

  useDeepCompareEffect(() => {
    setDataSource(data);
  }, [data]);

  const onSortEnd = useCallback(
    ({ oldIndex, newIndex }) => {
      if (oldIndex !== newIndex) {
        const newData = arrayMove<Task>(
          [...dataSource],
          oldIndex,
          newIndex,
        ).filter((el) => !!el);

        setDataSource(newData);
        if (onSort) {
          onSort(newData);
        }
      }
    },
    [dataSource, onSort],
  );

  const DraggableContainer = useCallback(
    (props) => (
      <Sortable
        useDragHandle
        lockAxis="y"
        helperClass={styles.row_dragging}
        onSortEnd={onSortEnd}
        {...props}
      />
    ),
    [onSortEnd],
  );

  const DraggableBodyRow = useCallback(
    ({ className, style, ...restProps }) => {
      // function findIndex base on Table rowKey props and should always be a right array index
      const index = dataSource.findIndex(
        (x) => x.taskId === restProps['data-row-key'],
      );

      return (
        <SortableItem
          index={index}
          {...restProps}
          className={`${
            restProps.children[0]?.props.record.private
              ? styles['private-row']
              : ''
          }`}
        />
      );
    },
    [dataSource],
  );

  const components = useMemo(
    () => ({
      body: {
        wrapper: DraggableContainer,
        row: DraggableBodyRow,
      },
    }),
    [DraggableBodyRow, DraggableContainer],
  );

  return (
    <AntTable
      size={size}
      rowKey="taskId"
      className={styles.table}
      pagination={false}
      columns={onSort ? [DragHandleCell, ...COLUMNS] : COLUMNS}
      dataSource={dataSource}
      tableLayout="fixed"
      components={components}
    />
  );
};
